import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FiFileText, FiSave, FiRefreshCw, FiArrowLeft, FiPlus, FiTrash2,
  FiAlertCircle, FiCheck, FiEdit
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

const API_URL = getApiUrl();

const DEFAULT_CONTRACT_TERMS = {
  title: "Conditions Générales de Location",
  introduction: "Les présentes conditions générales de location régissent les relations entre Just A Smile Events (ci-après « le Prestataire ») et le Client pour toute location de matériel photobooth et vidéobooth.",
  sections: [
    {
      id: '1',
      title: "Article 1 - Objet du contrat",
      content: "Le présent contrat a pour objet de définir les conditions de location du matériel photobooth/vidéobooth pour l'événement du Client."
    },
    {
      id: '2',
      title: "Article 2 - Durée de la location",
      content: "La durée de la location est définie dans le devis et la confirmation de réservation. Toute prolongation devra faire l'objet d'un accord préalable et pourra entraîner des frais supplémentaires."
    },
    {
      id: '3',
      title: "Article 3 - Tarifs et paiement",
      content: "Un acompte est exigé à la réservation pour confirmer celle-ci. Le montant de l'acompte est défini dans les paramètres de paiement. Le solde est payable au plus tard 7 jours avant l'événement. En cas de non-paiement du solde dans les délais, le Prestataire se réserve le droit d'annuler la réservation."
    },
    {
      id: '4',
      title: "Article 4 - Caution",
      content: "Une caution de 500€ est demandée et sera restituée dans les 48h suivant l'événement, sous réserve de la restitution du matériel en bon état."
    },
    {
      id: '5',
      title: "Article 5 - Responsabilité",
      content: "Le Client s'engage à utiliser le matériel conformément à sa destination. En cas de dommage ou de perte, le Client sera tenu responsable et la caution sera conservée en tout ou partie pour couvrir les réparations ou le remplacement."
    },
    {
      id: '6',
      title: "Article 6 - Annulation",
      content: "En cas d'annulation par le Client :\n- Plus de 30 jours avant l'événement : remboursement de l'acompte\n- Entre 15 et 30 jours : 50% de l'acompte conservé\n- Moins de 15 jours : acompte non remboursable"
    },
    {
      id: '7',
      title: "Article 7 - Force majeure",
      content: "Aucune des parties ne pourra être tenue responsable de l'inexécution de ses obligations en cas de force majeure (catastrophe naturelle, pandémie, grève, etc.)."
    },
    {
      id: '8',
      title: "Article 8 - Droit à l'image",
      content: "Le Client autorise le Prestataire à utiliser les photos et vidéos réalisées lors de l'événement à des fins promotionnelles, sauf mention contraire écrite."
    }
  ],
  signature_text: "Je reconnais avoir pris connaissance des conditions générales de location et les accepte sans réserve.",
  deposit_type: 'fixed', // 'percentage' or 'fixed'
  deposit_percentage: 30,
  deposit_fixed_amount: 100,
  caution_amount: 500,
  mypos_payment_link: 'https://mypos.com/vmp/btn/BEN1FLAFHS238'
};

export default function ContractTemplatePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contractTemplate, setContractTemplate] = useState(DEFAULT_CONTRACT_TERMS);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contracts/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.title) {
          setContractTemplate(data);
        }
      }
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contracts/template`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contractTemplate)
      });
      
      if (response.ok) {
        toast.success('Modèle de contrat sauvegardé');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (id, field, value) => {
    setContractTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === id ? { ...s, [field]: value } : s
      )
    }));
  };

  const addSection = () => {
    const newId = String(contractTemplate.sections.length + 1);
    setContractTemplate(prev => ({
      ...prev,
      sections: [...prev.sections, {
        id: newId,
        title: `Article ${newId} - Nouveau`,
        content: ''
      }]
    }));
  };

  const removeSection = (id) => {
    setContractTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== id)
    }));
  };

  const resetToDefault = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser le modèle de contrat par défaut ?')) {
      setContractTemplate(DEFAULT_CONTRACT_TERMS);
      toast.success('Modèle réinitialisé');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/contracts">
            <Button variant="outline" size="sm">
              <FiArrowLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Modèle de Contrat</h1>
            <p className="text-slate-500">Personnalisez les conditions générales de location</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefault}>
            <FiRefreshCw className="w-4 h-4 mr-1" />
            Réinitialiser
          </Button>
          <Button onClick={saveTemplate} disabled={saving} className="gap-2">
            {saving ? (
              <FiRefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <FiSave className="w-4 h-4" />
            )}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Modèle personnalisable</p>
              <p className="text-sm text-blue-700 mt-1">
                Ce modèle sera utilisé pour tous les nouveaux contrats générés. Les modifications n'affectent pas les contrats déjà créés.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="general">Informations générales</TabsTrigger>
          <TabsTrigger value="articles">Articles ({contractTemplate.sections.length})</TabsTrigger>
          <TabsTrigger value="financial">Paramètres financiers</TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>En-tête du contrat</CardTitle>
              <CardDescription>Titre et introduction du document</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titre du contrat</Label>
                <Input
                  value={contractTemplate.title}
                  onChange={(e) => setContractTemplate(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Conditions Générales de Location"
                />
              </div>
              <div>
                <Label>Introduction</Label>
                <Textarea
                  value={contractTemplate.introduction}
                  onChange={(e) => setContractTemplate(prev => ({ ...prev, introduction: e.target.value }))}
                  rows={4}
                  placeholder="Texte d'introduction..."
                />
              </div>
              <div>
                <Label>Texte de signature</Label>
                <Textarea
                  value={contractTemplate.signature_text}
                  onChange={(e) => setContractTemplate(prev => ({ ...prev, signature_text: e.target.value }))}
                  rows={2}
                  placeholder="Je reconnais avoir pris connaissance..."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Articles Tab */}
        <TabsContent value="articles" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              Gérez les articles de vos conditions générales
            </p>
            <Button onClick={addSection} variant="outline" className="gap-2">
              <FiPlus className="w-4 h-4" />
              Ajouter un article
            </Button>
          </div>

          <div className="space-y-4">
            {contractTemplate.sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <Input
                        value={section.title}
                        onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                        className="font-semibold text-lg border-0 p-0 h-auto focus-visible:ring-0"
                        placeholder="Titre de l'article"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSection(section.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={section.content}
                      onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                      rows={4}
                      placeholder="Contenu de l'article..."
                      className="resize-none"
                    />
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Paramètres par défaut</CardTitle>
              <CardDescription>
                Ces valeurs seront utilisées par défaut pour les nouveaux contrats
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Deposit Type Selection */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">Type d'acompte</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deposit_type"
                      value="percentage"
                      checked={contractTemplate.deposit_type !== 'fixed'}
                      onChange={() => setContractTemplate(prev => ({ ...prev, deposit_type: 'percentage' }))}
                      className="w-4 h-4 text-amber-600"
                    />
                    <span>Pourcentage du montant total</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deposit_type"
                      value="fixed"
                      checked={contractTemplate.deposit_type === 'fixed'}
                      onChange={() => setContractTemplate(prev => ({ ...prev, deposit_type: 'fixed' }))}
                      className="w-4 h-4 text-amber-600"
                    />
                    <span>Montant fixe</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Percentage Option */}
                {contractTemplate.deposit_type !== 'fixed' && (
                  <div>
                    <Label>Pourcentage d'acompte (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={contractTemplate.deposit_percentage}
                      onChange={(e) => setContractTemplate(prev => ({ 
                        ...prev, 
                        deposit_percentage: parseInt(e.target.value) || 0 
                      }))}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Acompte demandé à la réservation
                    </p>
                  </div>
                )}

                {/* Fixed Amount Option */}
                {contractTemplate.deposit_type === 'fixed' && (
                  <div>
                    <Label>Montant fixe de l'acompte (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={contractTemplate.deposit_fixed_amount || 100}
                      onChange={(e) => setContractTemplate(prev => ({ 
                        ...prev, 
                        deposit_fixed_amount: parseInt(e.target.value) || 0 
                      }))}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Montant fixe demandé à la réservation
                    </p>
                  </div>
                )}

                <div>
                  <Label>Montant de la caution (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={contractTemplate.caution_amount}
                    onChange={(e) => setContractTemplate(prev => ({ 
                      ...prev, 
                      caution_amount: parseInt(e.target.value) || 0 
                    }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Caution retenue pendant l'événement
                  </p>
                </div>
              </div>

              {/* MyPos Payment Link */}
              <div className="pt-4 border-t">
                <Label className="text-base font-semibold">Lien de paiement MyPos</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Ce lien sera utilisé pour le paiement de l'acompte par carte bancaire
                </p>
                <Input
                  type="url"
                  value={contractTemplate.mypos_payment_link || 'https://mypos.com/vmp/btn/BEN1FLAFHS238'}
                  onChange={(e) => setContractTemplate(prev => ({ 
                    ...prev, 
                    mypos_payment_link: e.target.value 
                  }))}
                  placeholder="https://mypos.com/vmp/btn/..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  Vous pouvez créer un nouveau lien sur votre espace MyPos
                </p>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex gap-3">
                  <FiAlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Note importante</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Ces paramètres peuvent être ajustés individuellement lors de la création de chaque contrat.
                      Les valeurs définies ici servent uniquement de valeurs par défaut.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
